import React, { useEffect, useState, useRef } from 'react';
import { BubbleInput, BubbleButton, BubbleAlert } from '@go-client/components/BubbleUI';
import { hasOwnProperty } from '@go-shared/lib/utils';
import { mapBy } from '@go-shared/lib/functional';
import { useServerInfo } from '@go-client/context/ServerInfoContext';
import {
  generateDefaultNewUITranslation,
  makeDefaultUITranslationDictionary,
  UITRANSLATION_DEFAULT_NAMESPACE,
} from '@go-shared/lib/uitranslation';
import { useActiveProperty } from '@go-client/context/ActivePropertyContext';
import { RouteComponentProps, Prompt } from 'react-router-dom';
import Translations from './Translations';
import TranslationBlock from '@go-client/components/TranslationBlock';
import {
  UITranslation,
  UITranslationDictionary,
  UITranslationTranslatable,
} from '@go-shared/types/uitranslation';
import {
  replaceUITranslationByNamespace,
  getUITranslations,
} from '@go-client/lib/api/uitranslation';
import {
  TranslationFilters,
  generateDefaultNewTranslation,
  makeTimeGroupingTranslationTransform,
} from '@go-shared/lib/translation';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { canModifyBrandTranslation } from '@go-shared/lib/permissions';
import { useUserInfo } from '@go-client/context/UserInfoContext';
import TranslationsSubNavigation from '@go-client/components/Navigations/TranslationsSubNavigation';
import { TimeGroupingTransforms, TimeGroupingTranslation } from '@go-shared/types/translation';
import { getTranslations, replaceTranslationByRefer } from '@go-client/lib/api/translation';
import { BrandTimeGrouping } from '@go-shared/types/brand';

const EVENT_RUNDOWN_WIDGET_GROUP_KEY = 'eventRundownWidget';

interface UITranslationGroup {
  key: string;
  name: string;
  dynamic?: boolean;
  items: Array<{
    key: UITranslationTranslatable;
    label: string;
  }>;
}

interface UITranslationDataBundle {
  uiGroups: UITranslationGroup[];
  uiTransls: UITranslation[];
  uiGroupsMap: { [key: string]: UITranslationGroup };
  uiTranslsMap: { [key: string]: UITranslation };
  timeGroupingTranslationsMap: { [refers: string]: TimeGroupingTranslation };
}

interface UITranslationForm {
  dirty: boolean;
  dictionary: UITranslationDictionary;
}

interface ChangeUITranslFormAction {
  active?: boolean;
  value?: string;
}

interface TimeGroupingTranslationForm {
  dirty: boolean;
  transformsById: { [timeGroupingId: string]: TimeGroupingTransforms };
}

interface ChangeTimeGroupingFormAction {
  active?: boolean;
  value?: string;
}

type UITranslationsPageProps = RouteComponentProps;

export default function UITranslations(props: UITranslationsPageProps) {
  const [loading, setLoading] = useState(true);
  const [dataBundle, setDataBundle] = useState<UITranslationDataBundle | undefined>(undefined);
  const [activeUIGroup, setActiveUIGroup] = useState<UITranslationGroup | undefined>(undefined);
  const [uiTranslForm, setUITranslForm] = useState<UITranslationForm | undefined>(undefined);
  const [timeGroupingForm, setTimeGroupingForm] = useState<
    TimeGroupingTranslationForm | undefined
  >(undefined);

  const contentRef = useRef<HTMLDivElement | null>(null);

  const { activeProperty } = useActiveProperty();
  const serverInfo = useServerInfo();
  const { languages, plugins, brand } = serverInfo;
  const { user } = useUserInfo();

  const timeGroupings: BrandTimeGrouping[] = brand.timeGroupings ?? [];

  useEffect(() => {
    setLoading(true);
    fetchDataBundle().catch(console.error);
  }, [activeProperty]);

  // this will refresh the currently active event template if data bundle refreshes
  useEffect(() => {
    if (!dataBundle) return;

    setActiveUIGroup(state => {
      const defaultUIGroup = dataBundle.uiGroups[0];
      if (!state) return defaultUIGroup;
      const refreshedUIGroup = dataBundle.uiGroupsMap[state.key];
      return refreshedUIGroup || defaultUIGroup;
    });
  }, [dataBundle]);

  // this will reset the form each time a new event template is clicked
  useEffect(() => {
    resetUITranslForm();
    resetTimeGroupingForm();
  }, [activeUIGroup]);

  const filteredUIGroups = !dataBundle ? [] : dataBundle.uiGroups;

  async function fetchDataBundle() {
    try {
      const uiGroups: UITranslationGroup[] = [];
      const uiTransls: UITranslation[] = await getUITranslations(activeProperty._id);

      if (plugins.findIndex(p => p.name === 'ddp') > -1) {
        uiGroups.push({
          key: 'ddp',
          name: 'DDP',
          items: [
            {
              key: 'DDP_EVENTS_LABEL',
              label: "The top navigation and mobile navigation title for the DDP's events section.",
            },
            {
              key: 'DDP_ACTIVITIES_LABEL',
              label:
                "The top navigation and mobile navigation title for the DDP's activities section.",
            },
            {
              key: 'DDP_HOURS_LABEL',
              label: "The top navigation and mobile navigation title for the DDP's hours section.",
            },
            {
              key: 'DDP_NO_CONTENT_LABEL',
              label: 'Text that shows when there is no content to display.',
            },
            {
              key: 'DDP_TODAY_LABEL',
              label: 'Text that shows when the day selected is current.',
            },
            {
              key: 'VENUE_RUNDOWN_CLOSED',
              label: 'Text that shows when there is no content to display.',
            },
          ],
        });
      }

      if (plugins.findIndex(p => p.name === 'navegante') > -1) {
        uiGroups.push({
          key: 'navegante',
          name: 'Mobile',
          items: [
            {
              key: 'MOBILE_NO_RESULTS_TEXT',
              label: 'The text that shows when there are no results found.',
            },
            {
              key: 'MOBILE_NO_FAVORITES_TEXT',
              label: 'The text that shows when the guest has no favorites.',
            },
            {
              key: 'MOBILE_LINEUP_LABEL',
              label: 'The label for the lineup page.',
            },
            {
              key: 'MOBILE_EXPLORE_LABEL',
              label: 'The label for the explore page.',
            },
            {
              key: 'MOBILE_OFFERS_LABEL',
              label: 'The label for the offers page.',
            },
            {
              key: 'MOBILE_MESSAGES_LABEL',
              label: 'The label for the messages page.',
            },
            {
              key: 'MOBILE_INFORMATION_LABEL',
              label: 'The label for the information page.',
            },
            {
              key: 'MOBILE_FAVORITES_LABEL',
              label: 'The label for the favorites page.',
            },
            {
              key: 'MOBILE_TODAY_LABEL',
              label: 'The label today.',
            },
            {
              key: 'MOBILE_TOMORROW_LABEL',
              label: 'The label tomorrow.',
            },
            {
              key: 'MOBILE_TRENDING_LABEL',
              label: 'The label for the trending filter.',
            },
            {
              key: 'MOBILE_UPCOMING_LABEL',
              label: 'The label for the upcoming events filter.',
            },
            {
              key: 'MOBILE_OPEN_NOW_LABEL',
              label: 'The label for the open venues filter.',
            },
            {
              key: 'MOBILE_VENUE_OPEN_LABEL',
              label: 'The label for an open venue status tag.',
            },
            {
              key: 'MOBILE_VENUE_CLOSED_LABEL',
              label: 'The label for a closed venue status tag.',
            },
          ],
        });
      }

      if (plugins.findIndex(p => p.name === 'naveganteboards') > -1) {
        uiGroups.push({
          key: 'naveganteboards',
          name: 'Boards',
          items: [
            {
              key: 'BOARD_TRENDING_LABEL',
              label: 'The label for the trending tag on GO Boards.',
            },
            {
              key: 'BOARD_OPEN_LABEL',
              label: 'The label for an open venue status tag on GO Boards.',
            },
            {
              key: 'BOARD_CLOSED_LABEL',
              label: 'The label for a closed venue status tag on GO Boards.',
            },
            {
              key: 'BOARD_EXPLORE_LABEL',
              label: 'The section header for the explore column on GO Boards.',
            },
            {
              key: 'BOARD_LINEUP_LABEL',
              label: 'The section header for the lineup column on GO Boards.',
            },
            {
              key: 'BOARD_OFFERS_LABEL',
              label: 'The section header for the offers column on GO Boards.',
            },
            {
              key: 'BOARD_TODAY_LABEL',
              label: 'The label for today on GO Boards.',
            },
            {
              key: 'BOARD_TOMORROW_LABEL',
              label: 'The label for tomorrow on GO Boards.',
            },
            {
              key: 'BOARD_QR_LABEL',
              label: 'The label shown next to the QR code on GO Boards.',
            },
            {
              key: 'BOARD_OTHER_TIMES_LABEL',
              label: 'The label for the other times carousel on GO Boards.',
            },
            {
              key: 'BOARD_AVAILABLE_AT_LABEL',
              label: 'The label for the "available at" carousel on GO Boards.',
            },
            {
              key: 'BOARD_EVENTS_LABEL',
              label: 'The label for the events carousel on GO Boards.',
            },
          ],
        });
      }

      uiGroups.push({
        key: EVENT_RUNDOWN_WIDGET_GROUP_KEY,
        name: 'Event Rundown Widget',
        dynamic: true,
        items: [],
      });

      const timeGroupingTranslations = await getTranslations(activeProperty._id, 'timeGrouping');

      setDataBundle({
        uiGroups,
        uiTransls,
        uiGroupsMap: mapBy(uiGroups, 'key'),
        uiTranslsMap: mapBy(uiTransls, 'namespace'),
        timeGroupingTranslationsMap: mapBy(timeGroupingTranslations, 'refers'),
      });
    } finally {
      setLoading(false);
    }
  }

  function resetTimeGroupingForm() {
    if (!dataBundle) {
      setTimeGroupingForm(undefined);
      return;
    }

    const transformsById: { [timeGroupingId: string]: TimeGroupingTransforms } = {};
    timeGroupings.forEach(tg => {
      transformsById[tg._id] = makeTimeGroupingTranslationTransform(
        languages.map(l => l.key),
        dataBundle.timeGroupingTranslationsMap[tg._id]
      );
    });

    setTimeGroupingForm({ dirty: false, transformsById });
  }

  function changeTimeGroupingForm(
    timeGroupingId: string,
    lang: string,
    change: ChangeTimeGroupingFormAction
  ) {
    if (!timeGroupingForm) return;
    const updatedForm = { ...timeGroupingForm, dirty: true };

    if (!hasOwnProperty(updatedForm.transformsById, timeGroupingId)) return;
    if (!hasOwnProperty(updatedForm.transformsById[timeGroupingId], lang)) return;

    if (change.active !== undefined) {
      updatedForm.transformsById[timeGroupingId][lang].active = change.active;
    }

    if (change.value !== undefined) {
      updatedForm.transformsById[timeGroupingId][lang].delta.name = change.value;
    }

    setTimeGroupingForm(updatedForm);
  }

  async function handleSubmitTimeGroupingForm() {
    if (!timeGroupingForm || !canModifyBrandTranslation(user, serverInfo)) return;

    await Promise.all(
      timeGroupings.map(tg => {
        const translation = generateDefaultNewTranslation(tg._id, 'timeGrouping');
        translation.transforms = timeGroupingForm.transformsById[tg._id];
        return replaceTranslationByRefer(tg._id, translation);
      })
    );

    await fetchDataBundle();
    setTimeGroupingForm(state => (state ? { ...state, dirty: false } : state));
  }

  function resetUITranslForm() {
    if (!dataBundle || !activeUIGroup) {
      setUITranslForm(undefined);
      return;
    }

    const dictionary = makeDefaultUITranslationDictionary(languages.map(l => l.key));
    const activeTransl = hasOwnProperty(dataBundle.uiTranslsMap, UITRANSLATION_DEFAULT_NAMESPACE)
      ? (dataBundle.uiTranslsMap.default as UITranslation)
      : undefined;

    dataBundle.uiGroups
      .map(g => g.items)
      .flat()
      .forEach(itm => {
        languages.forEach(lang => {
          const oldDict =
            activeTransl &&
            activeTransl.dictionary &&
            hasOwnProperty(activeTransl.dictionary, itm.key) &&
            hasOwnProperty(activeTransl.dictionary[itm.key].text, lang.key)
              ? activeTransl.dictionary[itm.key].text[lang.key]
              : undefined;

          if (!oldDict) return;

          dictionary[itm.key].text[lang.key] = oldDict;
        });
      });

    const formData: UITranslationForm = {
      dirty: false,
      dictionary,
    };

    setUITranslForm(formData);
  }

  function changeUITranslForm(
    lang: string,
    key: UITranslationTranslatable,
    change: ChangeUITranslFormAction
  ) {
    if (!uiTranslForm) return;
    const updatedForm = { ...uiTranslForm, dirty: true };

    if (
      !hasOwnProperty(updatedForm.dictionary, key) ||
      !hasOwnProperty(updatedForm.dictionary[key].text, lang)
    ) {
      return;
    }

    if (change.active !== undefined) {
      updatedForm.dictionary[key].text[lang].active = change.active;
    }

    if (change.value !== undefined) {
      updatedForm.dictionary[key].text[lang].value = change.value;
    }

    setUITranslForm(updatedForm);
  }

  async function handleSubmitUITranslForm() {
    if (!uiTranslForm || !activeUIGroup || !dataBundle) return;

    const translation = generateDefaultNewUITranslation(activeProperty._id);
    translation.dictionary = uiTranslForm.dictionary;

    await replaceUITranslationByNamespace(activeProperty._id, translation.namespace, translation);
    await fetchDataBundle();
  }

  function headerFn() {
    return <TranslationsSubNavigation active="miscellaneous" property={activeProperty} />;
  }

  function leftFn() {
    return (
      <>
        <div style={{ paddingTop: 'calc(40px + 0.75rem)' }}>
          {filteredUIGroups.map(uig => (
            <div
              key={uig.key}
              className={`list-item px-5 py-3 cursor-pointer ${
                activeUIGroup && activeUIGroup.key === uig.key ? 'active' : ''
              }`}
              onClick={() => setActiveUIGroup(uig)}
            >
              <div className="flex items-center">
                <div className="flex-grow min-w-0">
                  <div className="list-item-name font-semibold overflow-ellipsis">{uig.name}</div>
                </div>
                <FontAwesomeIcon
                  icon="chevron-right"
                  className="list-item-arrow ml-auto pl-3 flex-shrink-0"
                />
              </div>
            </div>
          ))}
          {filteredUIGroups.length < 1 && (
            <div className="grid items-center justify-center italic text-smoke bg-eggshell px-3 py-2">
              No translatable UI found
            </div>
          )}
        </div>
      </>
    );
  }

  function rightFn({ selectedLanguages }: TranslationFilters) {
    if (dataBundle && activeUIGroup && activeUIGroup.dynamic && timeGroupingForm) {
      return (
        <>
          <div style={{ marginBottom: '-1px' }}>
            {!canModifyBrandTranslation(user, serverInfo) && (
              <BubbleAlert className="mt-3 mx-3" variant="ocean">
                This miscellaneous cannot be modified unless you're in a brand instance and a
                brand admin.
              </BubbleAlert>
            )}
            {timeGroupings.length < 1 && (
              <div className="p-3 italic text-smoke">
                No time groupings configured - add some in Brand Details first.
              </div>
            )}
            {timeGroupings.map(tg => (
              <TranslationBlock
                key={tg._id}
                label={tg.name}
                value="Name shown as the Event Rundown widget's group heading."
                languages={selectedLanguages}
                headerCharCountFn={() => <></>}
              >
                {({ language }) => (
                  <div className="flex items-start">
                    <BubbleButton
                      inverse
                      variant={
                        !timeGroupingForm.transformsById[tg._id][language.key].active
                          ? 'smoke'
                          : 'navy'
                      }
                      className="px-1-i pt-2 mr-2 leading-none"
                      onClick={() =>
                        changeTimeGroupingForm(tg._id, language.key, {
                          active: !timeGroupingForm.transformsById[tg._id][language.key].active,
                        })
                      }
                      icon={
                        timeGroupingForm.transformsById[tg._id][language.key].active
                          ? 'eye'
                          : 'eye-slash'
                      }
                    />
                    <BubbleInput
                      type="text"
                      block
                      className="flex-grow"
                      inputClassName={
                        !timeGroupingForm.transformsById[tg._id][language.key].active
                          ? 'italic text-smoke'
                          : ''
                      }
                      value={timeGroupingForm.transformsById[tg._id][language.key].delta.name}
                      onValue={val =>
                        changeTimeGroupingForm(tg._id, language.key, { value: val })
                      }
                    />
                  </div>
                )}
              </TranslationBlock>
            ))}
          </div>
          <div className="sticky bottom-0 p-3 bg-eggshell border-t">
            <BubbleButton
              onClick={() => handleSubmitTimeGroupingForm()}
              variant="navy"
              disabled={!timeGroupingForm.dirty || !canModifyBrandTranslation(user, serverInfo)}
              icon="floppy-disk"
            >
              Save Changes
            </BubbleButton>
            <BubbleButton
              variant="slate"
              disabled={!timeGroupingForm.dirty}
              inverse
              onClick={() => resetTimeGroupingForm()}
              icon="rotate-left"
            >
              Reset
            </BubbleButton>
          </div>
        </>
      );
    }

    return (
      <>
        {dataBundle && activeUIGroup && !activeUIGroup.dynamic && uiTranslForm && (
          <>
            <div style={{ marginBottom: '-1px' }}>
              {!canModifyBrandTranslation(user, serverInfo) && (
                <BubbleAlert className="mt-3 mx-3" variant="ocean">
                  This miscellaneous cannot be modified unless you're in a brand instance and a
                  brand admin.
                </BubbleAlert>
              )}
              {activeUIGroup.items.map(itm => (
                <TranslationBlock
                  key={itm.key}
                  label={itm.key}
                  value={itm.label}
                  languages={selectedLanguages}
                  headerCharCountFn={() => <></>}
                >
                  {({ language }) => (
                    <div className="flex items-start">
                      <BubbleButton
                        inverse
                        variant={
                          !uiTranslForm.dictionary[itm.key].text[language.key].active
                            ? 'smoke'
                            : 'navy'
                        }
                        className="px-1-i pt-2 mr-2 leading-none"
                        onClick={() =>
                          changeUITranslForm(language.key, itm.key, {
                            active: !uiTranslForm.dictionary[itm.key].text[language.key].active,
                          })
                        }
                        icon={
                          uiTranslForm.dictionary[itm.key].text[language.key].active
                            ? 'eye'
                            : 'eye-slash'
                        }
                      />
                      <BubbleInput
                        type="text"
                        block
                        className="flex-grow"
                        inputClassName={
                          !uiTranslForm.dictionary[itm.key].text[language.key].active
                            ? 'italic text-smoke'
                            : ''
                        }
                        value={uiTranslForm.dictionary[itm.key].text[language.key].value}
                        onValue={val => changeUITranslForm(language.key, itm.key, { value: val })}
                      />
                    </div>
                  )}
                </TranslationBlock>
              ))}
            </div>
            <div className="sticky bottom-0 p-3 bg-eggshell border-t">
              <BubbleButton
                onClick={() => handleSubmitUITranslForm()}
                variant="navy"
                disabled={!uiTranslForm.dirty || !canModifyBrandTranslation(user, serverInfo)}
                icon="floppy-disk"
              >
                Save Changes
              </BubbleButton>
              <BubbleButton
                variant="slate"
                disabled={!uiTranslForm.dirty}
                inverse
                onClick={() => resetUITranslForm()}
                icon="rotate-left"
              >
                Reset
              </BubbleButton>
            </div>
          </>
        )}
      </>
    );
  }

  // this adds a check to prevent closing tab if unsaved changes
  useEffect(() => {
    if ((uiTranslForm && uiTranslForm.dirty) || (timeGroupingForm && timeGroupingForm.dirty)) {
      window.onbeforeunload = () => true;
    } else {
      window.onbeforeunload = null;
    }
  });

  return (
    <>
      <Prompt
        when={(uiTranslForm && uiTranslForm.dirty) || (timeGroupingForm && timeGroupingForm.dirty)}
        message="You have unsaved changes! Are you sure you want to leave?"
      />
      <Translations
        {...props}
        category="miscellaneous"
        loading={loading}
        contentRef={contentRef}
        property={activeProperty}
        languages={languages}
        leftFn={leftFn}
        rightFn={rightFn}
        headerFn={headerFn}
      />
    </>
  );
}
